// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import GppGoodIcon from '@mui/icons-material/GppGood'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'

// Custom
import Section from '@/components/sections/Section'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody, getFontBodySmall, getFontH2 } from '@/assets/theme/fonts'

type Card = {
  number: string
  icon: ReactElement
  title: string
  intro: string
  bullets: string[]
}

export default function ChallengesSection() {
  const { t } = useTranslation('servicesOtCybersecurity')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const mobileMode = w < breakpoints.desktopToMobile

  const iconSx = { fontSize: '1.9rem', color: themeColors.kroemtech.dunkelblau }

  const cards: Card[] = [
    {
      number: '01',
      icon: <ManageSearchIcon sx={iconSx} />,
      title: t('challenges.cards.01.title'),
      intro: t('challenges.cards.01.intro'),
      bullets: [
        t('challenges.cards.01.bullets.1'),
        t('challenges.cards.01.bullets.2'),
        t('challenges.cards.01.bullets.3'),
        t('challenges.cards.01.bullets.4'),
        t('challenges.cards.01.bullets.5')
      ]
    },
    {
      number: '02',
      icon: <GppGoodIcon sx={iconSx} />,
      title: t('challenges.cards.02.title'),
      intro: t('challenges.cards.02.intro'),
      bullets: [
        t('challenges.cards.02.bullets.1'),
        t('challenges.cards.02.bullets.2'),
        t('challenges.cards.02.bullets.3'),
        t('challenges.cards.02.bullets.4'),
        t('challenges.cards.02.bullets.5'),
        t('challenges.cards.02.bullets.6'),
        t('challenges.cards.02.bullets.7')
      ]
    },
    {
      number: '03',
      icon: <TrendingDownIcon sx={iconSx} />,
      title: t('challenges.cards.03.title'),
      intro: t('challenges.cards.03.intro'),
      bullets: [
        t('challenges.cards.03.bullets.1'),
        t('challenges.cards.03.bullets.2'),
        t('challenges.cards.03.bullets.3'),
        t('challenges.cards.03.bullets.4'),
        t('challenges.cards.03.bullets.5'),
        t('challenges.cards.03.bullets.6'),
        t('challenges.cards.03.bullets.7')
      ]
    }
  ]

  const getCard = (card: Card) => (
    <Box
      key={card.number}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: themeColors.section.light,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        padding: '1.75rem',
        boxSizing: 'border-box'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <Box
          sx={{
            flexShrink: 0,
            width: '3.4rem',
            height: '3.4rem',
            borderRadius: '50%',
            background: themeColors.kroemtech.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {card.icon}
        </Box>
        <Box>
          <Typography
            component="p"
            sx={{
              fontSize: '1.4rem',
              fontWeight: 700,
              lineHeight: 1.1,
              color: themeColors.success.main
            }}
          >
            {card.number}
          </Typography>
          <Typography
            variant="h3"
            component="h3"
            sx={{
              fontSize: '1.05rem',
              fontWeight: 700,
              lineHeight: 1.25,
              color: themeColors.kroemtech.dunkelblau
            }}
          >
            {card.title}
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: getFontBody(),
          lineHeight: 1.6,
          color: themeColors.gray.defaultText,
          marginTop: '1.25rem'
        }}
      >
        {card.intro}
      </Typography>

      <Box
        sx={{
          borderTop: `1px solid ${themeColors.gray.lighter}`,
          margin: '1.25rem 0'
        }}
      />

      <Box
        component="ul"
        sx={{ listStyle: 'none', margin: 0, padding: 0, flex: 1 }}
      >
        {card.bullets.map(bullet => (
          <Box
            component="li"
            key={bullet}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.6rem',
              marginBottom: '0.6rem'
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: themeColors.success.main,
                marginTop: '0.5rem'
              }}
            />
            <Typography
              sx={{
                fontSize: getFontBodySmall(),
                lineHeight: 1.5,
                color: themeColors.gray.defaultText
              }}
            >
              {bullet}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )

  const getFooter = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: mobileMode ? 'flex-start' : 'center',
        gap: '1.25rem',
        // A tint of the brand green - the palette has no light green of its own.
        background: 'rgba(45, 206, 137, 0.08)',
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        padding: '1.5rem 1.75rem',
        marginTop: '2rem'
      }}
    >
      <ShieldOutlinedIcon
        sx={{ fontSize: '2.25rem', color: themeColors.success.darker }}
      />
      <Box>
        <Typography
          sx={{
            fontSize: getFontBody(),
            fontWeight: 700,
            color: themeColors.kroemtech.dunkelblau,
            marginBottom: '0.25rem'
          }}
        >
          {t('challenges.footer.title')}
        </Typography>
        <Typography
          sx={{
            fontSize: getFontBody(),
            lineHeight: 1.6,
            color: themeColors.gray.defaultText
          }}
        >
          {t('challenges.footer.text')}
        </Typography>
      </Box>
    </Box>
  )

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          fontSize: getFontBodySmall(),
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: themeColors.success.main,
          marginBottom: '0.5rem'
        }}
      >
        {t('challenges.eyebrow')}
      </Typography>
      <Box
        sx={{
          width: '2.5rem',
          borderTop: `3px solid ${themeColors.success.main}`,
          marginBottom: '1rem'
        }}
      />

      <Typography
        variant="h2"
        component="h2"
        fontSize={getFontH2()}
        sx={{
          fontWeight: 700,
          color: themeColors.kroemtech.dunkelblau,
          marginBottom: '1.25rem'
        }}
      >
        {t('challenges.title')}
      </Typography>

      <Typography
        sx={{
          fontSize: getFontBody(),
          lineHeight: 1.7,
          color: themeColors.gray.defaultText,
          maxWidth: '48rem',
          marginBottom: '2.5rem'
        }}
      >
        {t('challenges.description')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: mobileMode ? 'repeat(1, 1fr)' : 'repeat(3, 1fr)',
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {cards.map(card => getCard(card))}
      </Box>

      {getFooter()}
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Box sx={{ borderTop: `1px solid ${themeColors.gray.lighter}` }}>
      <Section
        background={themeColors.kroemtech.background}
        paddingTop="4rem"
        paddingBottom="5rem"
        content={getContent()}
      />
    </Box>
  )
}
